import { useState, useEffect } from 'react';
import {
  Grid,
  LinearProgress,
  Select,
  OutlinedInput,
  MenuItem,
  Box,
  IconButton,
} from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import RefreshIcon from '@material-ui/icons/Refresh';
import { useTheme } from '@material-ui/styles';
import { Link, useHistory } from 'react-router-dom';
import {
  ResponsiveContainer,
  ComposedChart,
  Line,
  YAxis,
  XAxis,
  Tooltip
} from 'recharts';
import Alert from '@material-ui/lab/Alert';

// styles
import useStyles from './styles';

import Widget from '../../components/Widget';
import PageTitle from '../../components/PageTitle';
import { Typography } from '../../components/Wrappers';
import Dot from '../../components/Sidebar/components/Dot';
import Table from './components/Table/Table';
import MonthPicker from './components/MonthPicker';
import CertExpiryWarning from '../../components/CertExpiryWarning/CertExpiryWarning';
import { toCurrency } from '../../utils/mask';
import api from '../../services/api';
import { dateToBD, getMonth } from '../../utils/dateFormatter';

const CERT_EXPIRY_WARNING_DAYS = 14;

export default function Dashboard(props) {
  const classes = useStyles();
  const theme = useTheme();
  const history = useHistory();

  // Certificate expiry warning state
  const [certWarning, setCertWarning] = useState({
    open: false,
    bankName: '',
    expirationDate: '',
  });

  // local
  const [mainChartState, setMainChartState] = useState(6);
  const [resumoMesAno, setResumoMesAno] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });
  const [sumarios, setSumarios] = useState({
    loading: false,
    erro: null,
    pagos: null,
    vencidos: null,
    baixados: null,
    pendentes: null
  });

  const [historicoGrafico, setHistoricoGrafico] = useState({
    loading: false,
    erro: null,
    dados: null
  });

  const [ultimasFaturas, setUltimasFaturas] = useState({
    loading: false,
    erro: null,
    dados: null
  });

  function getMesAtualInicioEFim(refDate = new Date()) {
    const inicio = new Date(refDate.getFullYear(), refDate.getMonth(), 1);
    const fim = new Date(refDate.getFullYear(), refDate.getMonth() + 1, 0);

    return {
      inicio,
      fim,
    };
  }

  function getFaturasUrl(estadoIds) {
    const { inicio, fim } = getMesAtualInicioEFim(resumoMesAno);
    const params = new URLSearchParams();

    params.set('pagina', 1);
    params.set('criado_em_inicio', dateToBD(inicio));
    params.set('criado_em_fim', dateToBD(fim));

    estadoIds.forEach((id) => {
      params.append('id_estado_fatura', id);
    });

    return `/app/faturas?${params.toString()}`;
  }

  useEffect(() => {
    faturasRequest();
    checkCertExpiry();
  }, []);

  async function checkCertExpiry() {
    try {
      const { data } = await api.get('/configuracoes-bancarias');
      if (data && data.length > 0) {
        const config = data[0]; // Assuming first config is for 'inter'
        const bankName = config.banco || 'Inter';
        const expirationDate = config.certificado_expiracao;

        if (expirationDate) {
          const today = new Date();
          const expiry = new Date(expirationDate);
          const diffTime = expiry - today;
          const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

          // Check if expiring within warning threshold
          if (diffDays <= CERT_EXPIRY_WARNING_DAYS && diffDays > 0) {
            // Check if user dismissed this warning
            const dismissKey = `cert-warning-dismissed-${bankName}-${expirationDate}`;
            const dismissed = localStorage.getItem(dismissKey);

            if (!dismissed) {
              setCertWarning({
                open: true,
                bankName,
                expirationDate,
              });
            }
          }
        }
      }
    } catch (error) {
      // Silently fail - don't block dashboard if config check fails
      console.error('Failed to check certificate expiry:', error);
    }
  }

  function handleCertWarningClose() {
    setCertWarning({ ...certWarning, open: false });
  }

  function handleCertWarningRedirect() {
    history.push('/app/sistema/configuracoes-bancarias');
  }

  useEffect(() => {
    sumariosRequest();
  }, [resumoMesAno]);

  useEffect(() => {
    historicoGraficoRequest();
  }, [mainChartState]);

  function update() {
    sumariosRequest();
    faturasRequest();
    historicoGraficoRequest();
  }

  async function sumariosRequest() {
    setSumarios((prev) => ({ ...prev, loading: true, erro: null }));
    const dataRef = new Date(resumoMesAno.getFullYear(), resumoMesAno.getMonth(), 1);
    try {
      const { data } = await api.get(`/fatura/resumo?data=${dateToBD(dataRef)}`);
      setSumarios((prev) => ({
        ...prev,
        loading: false,
        pagos: data.pagos,
        pendentes: data.pendentes,
        vencidos: data.expirados,
        baixados: data.baixados,
      }));
    } catch (error) {
      setSumarios((prev) => ({ ...prev, loading: false, erro: true }));
    }
  }

  async function historicoGraficoRequest() {
    setHistoricoGrafico((prev) => ({ ...prev, loading: true, erro: null }));
    const today = new Date();
    const calcularMeses = new Date()
      .setMonth(new Date().getMonth() - (mainChartState || 3));

    try {
      const { data } = await api
        .get(`/fatura/resumo?dataInicio=${dateToBD(calcularMeses)}&dataFim=${dateToBD(today)}`);
      const dadosComProximoMes = (() => {
        if (!data || !Array.isArray(data) || data.length === 0) {
          return data;
        }

        const proximoMesData = new Date(today.getFullYear(), today.getMonth() + 1, 1);

        return [
          ...data,
          {
            mesAno: getMonth(proximoMesData),
            expirados: 0,
            pendentes: 0,
            baixados: 0,
            pagos: 0,
          },
        ];
      })();
      setHistoricoGrafico((prev) => ({
        ...prev,
        loading: false,
        dados: dadosComProximoMes
      }));
    } catch (error) {
      setHistoricoGrafico((prev) => ({ ...prev, loading: false, erro: true }));
    }
  }

  async function faturasRequest() {
    setUltimasFaturas((prev) => ({ ...prev, loading: true, erro: null }));
    try {
      const { data } = await api
        .get('/fatura/ultimas');
      setUltimasFaturas((prev) => ({
        ...prev,
        loading: false,
        dados: data
      }));
    } catch (error) {
      setUltimasFaturas((prev) => ({ ...prev, loading: false, erro: true }));
    }
  }

  return (
    <>
      <PageTitle
        title="Resumo"
        button={(
          <Box display="flex" alignItems="center" className={classes.pageTitleActions}>
            <MonthPicker
              value={resumoMesAno}
              onChange={setResumoMesAno}
              className={classes.resumoMonthPicker}
            />
            <IconButton
              aria-label="Atualizar"
              onClick={update}
              className={classes.refreshButton}
              size="small"
            >
              <RefreshIcon />
            </IconButton>
          </Box>
        )}
      />
      <Grid container spacing={4}>
        <Grid item lg={3} md={4} sm={6} xs={12}>
          <Widget
            upperTitle
            disableWidgetMenu
            bodyClass={classes.fullHeightBody}
            className={classes.card}
            header={(
              <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
                <Typography variant="h5" color="textSecondary" noWrap>
                  Pendentes
                </Typography>
                <Link to={getFaturasUrl([1])} aria-label="ver faturas pendentes">
                  <IconButton color="primary" size="small">
                    <SearchIcon />
                  </IconButton>
                </Link>
              </Box>
            )}
          >
            <div className={classes.visitsNumberContainer}>
              <Grid container item alignItems="center">
                <Grid item xs={12}>
                  {sumarios.loading ? (
                    <LinearProgress />
                  ) : (
                    <Typography size="xl" weight="medium" noWrap>
                      {sumarios.pendentes && sumarios.pendentes.total}
                    </Typography>
                  )}
                </Grid>
              </Grid>
            </div>
            {sumarios.erro ? (
              <Alert severity="error">Erro ao listar</Alert>
            ) : (
              <Grid
                container
                direction="row"
                justify="space-between"
                alignItems="center"
              >
                {sumarios.loading ? (
                  <LinearProgress />
                ) : (
                  <>
                    <Grid item xs={6}>
                      <Typography color="text" colorBrightness="secondary" noWrap>
                        Valor
                      </Typography>

                      <Typography size="md">
                        {sumarios.pendentes && toCurrency(sumarios.pendentes.valorTotal, true)}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography align="right" color="text" colorBrightness="secondary" noWrap>
                        Mês
                      </Typography>

                      <Typography align="right" size="md">
                        {sumarios.pendentes && getMonth(resumoMesAno)}
                      </Typography>
                    </Grid>
                  </>
                )}
              </Grid>
            )}
          </Widget>
        </Grid>
        <Grid item lg={3} md={4} sm={6} xs={12}>
          <Widget
            upperTitle
            disableWidgetMenu
            bodyClass={classes.fullHeightBody}
            className={classes.card}
            header={(
              <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
                <Typography variant="h5" color="textSecondary" noWrap>
                  Pagos
                </Typography>
                <Link to={getFaturasUrl([2])} aria-label="ver faturas pagas">
                  <IconButton color="primary" size="small">
                    <SearchIcon />
                  </IconButton>
                </Link>
              </Box>
            )}
          >
            <div className={classes.visitsNumberContainer}>
              <Grid container item alignItems="center">
                <Grid item xs={12}>
                  {sumarios.loading ? (
                    <LinearProgress />
                  ) : (
                    <Typography size="xl" weight="medium" noWrap>
                      {sumarios.pagos && sumarios.pagos.total}
                    </Typography>
                  )}
                </Grid>
              </Grid>
            </div>
            {sumarios.erro ? (
              <Alert severity="error">Erro ao listar</Alert>
            ) : (
              <Grid
                container
                direction="row"
                justify="space-between"
                alignItems="center"
              >
                {sumarios.loading ? (
                  <LinearProgress />
                ) : (
                  <>
                    <Grid item xs={6}>
                      <Typography color="text" colorBrightness="secondary" noWrap>
                        Valor
                      </Typography>

                      <Typography size="md">
                        {sumarios.pagos && toCurrency(sumarios.pagos.valorTotal, true)}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography align="right" color="text" colorBrightness="secondary" noWrap>
                        Mês
                      </Typography>

                      <Typography align="right" size="md">
                        {sumarios.pagos && getMonth(resumoMesAno)}
                      </Typography>
                    </Grid>
                  </>
                )}
              </Grid>
            )}
          </Widget>
        </Grid>
        <Grid item lg={3} md={4} sm={6} xs={12}>
          <Widget
            upperTitle
            disableWidgetMenu
            bodyClass={classes.fullHeightBody}
            className={classes.card}
            header={(
              <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
                <Typography variant="h5" color="textSecondary" noWrap>
                  Baixados
                </Typography>
                <Link to={getFaturasUrl([6])} aria-label="ver faturas baixadas">
                  <IconButton color="primary" size="small">
                    <SearchIcon />
                  </IconButton>
                </Link>
              </Box>
            )}
          >
            <div className={classes.visitsNumberContainer}>
              <Grid container item alignItems="center">
                <Grid item xs={12}>
                  {sumarios.loading ? (
                    <LinearProgress />
                  ) : (
                    <Typography size="xl" weight="medium" noWrap>
                      {sumarios.baixados && sumarios.baixados.total}
                    </Typography>
                  )}
                </Grid>
              </Grid>
            </div>
            {sumarios.erro ? (
              <Alert severity="error">Erro ao listar</Alert>
            ) : (
              <Grid
                container
                direction="row"
                justify="space-between"
                alignItems="center"
              >
                {sumarios.loading ? (
                  <LinearProgress />
                ) : (
                  <>
                    <Grid item xs={6}>
                      <Typography color="text" colorBrightness="secondary" noWrap>
                        Valor
                      </Typography>

                      <Typography size="md">
                        {sumarios.baixados && toCurrency(sumarios.baixados.valorTotal, true)}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography align="right" color="text" colorBrightness="secondary" noWrap>
                        Mês
                      </Typography>

                      <Typography align="right" size="md">
                        {sumarios.baixados && getMonth(resumoMesAno)}
                      </Typography>
                    </Grid>
                  </>
                )}
              </Grid>
            )}
          </Widget>
        </Grid>
        <Grid item lg={3} md={4} sm={6} xs={12}>
          <Widget
            upperTitle
            disableWidgetMenu
            bodyClass={classes.fullHeightBody}
            className={classes.card}
            header={(
              <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
                <Typography variant="h5" color="textSecondary" noWrap>
                  Vencidos
                </Typography>
                <Link to={getFaturasUrl([5])} aria-label="ver faturas vencidas">
                  <IconButton color="primary" size="small">
                    <SearchIcon />
                  </IconButton>
                </Link>
              </Box>
            )}
          >
            <div className={classes.visitsNumberContainer}>
              <Grid container item alignItems="center">
                <Grid item xs={12}>
                  {sumarios.loading ? (
                    <LinearProgress />
                  ) : (
                    <Typography size="xl" weight="medium" noWrap>
                      {sumarios.vencidos && sumarios.vencidos.total}
                    </Typography>
                  )}
                </Grid>
              </Grid>
            </div>
            {sumarios.erro ? (
              <Alert severity="error">Erro ao listar</Alert>
            ) : (
              <Grid
                container
                direction="row"
                justify="space-between"
                alignItems="center"
              >
                {sumarios.loading ? (
                  <LinearProgress />
                ) : (
                  <>
                    <Grid item xs={6}>
                      <Typography color="text" colorBrightness="secondary" noWrap>
                        Valor
                      </Typography>

                      <Typography size="md">
                        {sumarios.vencidos && toCurrency(sumarios.vencidos.valorTotal, true)}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography align="right" color="text" colorBrightness="secondary" noWrap>
                        Mês
                      </Typography>

                      <Typography align="right" size="md">
                        {sumarios.vencidos && getMonth(resumoMesAno)}
                      </Typography>
                    </Grid>
                  </>
                )}
              </Grid>
            )}
          </Widget>
        </Grid>
        <Grid item xs={12}>
          <Widget
            bodyClass={classes.mainChartBody}
            header={(
              <div className={classes.mainChartHeader}>
                <Typography
                  variant="h5"
                  color="text"
                  colorBrightness="secondary"
                >
                  Histórico de faturas
                </Typography>
                {!historicoGrafico.erro && !historicoGrafico.loading && (
                  <>
                    <div className={classes.mainChartHeaderLabels}>
                      <div className={classes.mainChartHeaderLabel}>
                        <Dot color="info" />
                        <Typography className={classes.mainChartLegentElement}>
                          Vencidos
                        </Typography>
                      </div>
                      <div className={classes.mainChartHeaderLabel}>
                        <Dot color="warning" />
                        <Typography className={classes.mainChartLegentElement}>
                          Pendentes
                        </Typography>
                      </div>
                      <div className={classes.mainChartHeaderLabel}>
                        <Dot color="text" />
                        <Typography className={classes.mainChartLegentElement}>
                          Baixados
                        </Typography>
                      </div>
                      <div className={classes.mainChartHeaderLabel}>
                        <Dot color="primary" />
                        <Typography className={classes.mainChartLegentElement}>
                          Pagos
                        </Typography>
                      </div>
                    </div>
                    <Select
                      value={mainChartState}
                      onChange={(e) => setMainChartState(e.target.value)}
                      input={(
                        <OutlinedInput
                          labelWidth={0}
                          classes={{
                            notchedOutline: classes.mainChartSelectRoot,
                            input: classes.mainChartSelect,
                          }}
                        />
                      )}
                      autoWidth
                    >
                      <MenuItem value={3}>Últimos 3 meses</MenuItem>
                      <MenuItem value={6}>Últimos 6 meses</MenuItem>
                      <MenuItem value={12}>Últimos 12 meses</MenuItem>
                    </Select>
                  </>
                )}

              </div>
            )}
          >
            {!historicoGrafico.loading
              && !historicoGrafico.erro
              && historicoGrafico.dados
              && (
                <ResponsiveContainer width="100%" height={350}>
                  <ComposedChart
                    margin={{
                      top: 0, right: 24, left: 8, bottom: 0
                    }}
                    data={historicoGrafico.dados}
                  >
                    <Tooltip />
                    <YAxis
                      // ticks={[0, 2500, 5000, 7500]}
                      tick={{ fill: `${theme.palette.text.primary}80`, fontSize: 14 }}
                      stroke={`${theme.palette.text.primary}80`}
                      tickLine={false}
                    />
                    <XAxis
                      dataKey="mesAno"
                      tick={{ fill: `${theme.palette.text.primary}80`, fontSize: 14 }}
                      stroke={`${theme.palette.text.primary}80`}
                      tickLine={false}
                    />
                    <Line
                      type="linear"
                      dataKey="expirados"
                      textRendering="vencidos"
                      textAnchor="vencidos"
                      stroke={theme.palette.info.main}
                      strokeWidth={2}
                      dot={{
                        stroke: theme.palette.info.dark,
                        strokeWidth: 2,
                        fill: theme.palette.info.main,
                      }}
                    />
                    <Line
                      type="linear"
                      dataKey="pendentes"
                      stroke={theme.palette.warning.dark}
                      strokeWidth={2}
                      dot={{
                        stroke: theme.palette.warning.dark,
                        strokeWidth: 2,
                        fill: theme.palette.warning.dark,
                      }}
                    />
                    <Line
                      type="linear"
                      dataKey="baixados"
                      stroke={theme.palette.text.primary}
                      strokeWidth={2}
                      dot={{
                        stroke: theme.palette.text.primary,
                        strokeWidth: 2,
                        fill: theme.palette.text.primary,
                      }}
                    />
                    <Line
                      type="linear"
                      dataKey="pagos"
                      stroke={theme.palette.primary.main}
                      strokeWidth={2}
                      dot={{
                        stroke: theme.palette.primary.dark,
                        strokeWidth: 2,
                        fill: theme.palette.primary.main,
                      }}
                    />

                  </ComposedChart>

                </ResponsiveContainer>
              )}

            {!historicoGrafico.erro && historicoGrafico.loading && (
              <LinearProgress />
            )}

            {historicoGrafico.erro && !historicoGrafico.loading && (
              <Alert severity="error">Erro ao listar</Alert>
            )}
          </Widget>
        </Grid>
        <Grid item xs={12}>
          <Widget
            title="Últimos boletos registrados"
            upperTitle
            noBodyPadding
            bodyClass={classes.tableWidget}
            disableWidgetMenu
          >
            {ultimasFaturas.loading && !ultimasFaturas.erro && (
              <Box
                p={5}
              >
                <LinearProgress />
              </Box>
            )}
            {!ultimasFaturas.loading && ultimasFaturas.erro && (
              <Box
                p={2}
              >
                <Alert severity="error">Erro ao listar</Alert>
              </Box>
            )}
            {!ultimasFaturas.loading
              && !ultimasFaturas.erro
              && ultimasFaturas.dados
              && (
                <Table data={ultimasFaturas.dados} />
              )}
          </Widget>
        </Grid>
      </Grid>

      <CertExpiryWarning
        open={certWarning.open}
        onClose={handleCertWarningClose}
        bankName={certWarning.bankName}
        expirationDate={certWarning.expirationDate}
        onRedirect={handleCertWarningRedirect}
      />
    </>
  );
}
